import type { ZodError, ZodType, ZodTypeDef } from "zod"

import { isDefined } from "./Type"

/*
 * Thin wrapper around fetch() that ensures type safety via body validators and explicitly typed error results.
 *
 * Example:
 * request(
    "https://www.anapioficeandfire.com/api/books",
    {
      okType: array(object({ url: string() })),
      signal: controller.signal
    }
  )
 */
export async function request<BODY>(
  url: string | URL,
  options: RequestOptions<BODY>
): Promise<BODY> {
  const {
    okType,
    signal: fetchSignal,
    headers: fetchHeaders,
    body: fetchBody,
    timeout = 15_000,
    ...fetchOptions
  } = options

  const headers = new Headers(fetchHeaders)

  if (!headers.has("Content-Type") && fetchBody)
    headers.set("Content-Type", "application/json")

  // https://developer.mozilla.org/en-US/docs/Web/API/FormData/Using_FormData_Objects#sending_files_using_a_formdata_object
  if (headers.get("Content-Type") === "multipart/form-data")
    headers.delete("Content-Type")

  const token = getBearerToken()
  if (token) headers.set("Authorization", `Bearer ${token}`)

  const body =
    headers.get("Content-Type") === "application/json"
      ? JSON.stringify(fetchBody)
      : (fetchBody as RequestInit["body"])

  let timedOut = false

  try {
    // An artificial timeout on a mutative HTTP method can be dangerous because the side effect
    // might still happen on the server but the client won't know till it's refreshed.
    const canTimeout = !options.method || options.method === "GET"

    const timeoutController = canTimeout ? new AbortController() : undefined
    const signal = mergeAbortSignals(
      [timeoutController?.signal, fetchSignal].filter(isDefined)
    )

    if (timeoutController)
      setTimeout(() => {
        timedOut = true
        timeoutController.abort()
      }, timeout)

    const response = await fetch(url, {
      ...fetchOptions,
      body,
      headers,
      signal
    })

    const { status } = response
    const textResponse = response.clone()
    const responseBody: unknown = await response
      .json()
      .catch((_) => textResponse.text())

    if (response.ok) {
      const parsed = okType.safeParse(responseBody)

      if (!parsed.success)
        throw new RequestError({
          type: "validation",
          status,
          cause: parsed.error
        })

      return parsed.data
    } else {
      throw new RequestError({ type: "http", status, body: responseBody })
    }
  } catch (err) {
    // If it's our own Error, just rethrow.
    if (isRequestError(err)) throw err

    // Recover abort and timeout errors with a more specific type.
    if (isAbortError(err))
      throw new RequestError({ type: timedOut ? "timeout" : "abort" })

    // Unexpected or impossible to catch sooner error (CORS, etc)
    throw new RequestError({ type: "unknown", cause: err })
  }
}

interface RequestOptions<BODY> extends Omit<RequestInit, "body"> {
  okType: ZodType<BODY, ZodTypeDef, unknown>
  timeout?: number
  body?: RequestInit["body"] | object
}

class RequestError extends Error {
  constructor(public detail: RequestErrorDetail) {
    super(JSON.stringify(detail, null, 2))
    this.name = "RequestError"
  }
}

export type RequestErrorDetail =
  | { type: "http"; status: number; body: unknown }
  | { type: "abort" }
  | { type: "timeout" }
  | { type: "unknown"; cause: unknown }
  | { type: "validation"; status: number; cause: ZodError<unknown> }

export function isRequestError(param: unknown): param is RequestError {
  return param instanceof RequestError
}

export function isAuthError(param: unknown) {
  if (!isRequestError(param)) return false

  if (param.detail.type !== "http") return false

  // Some of our services return 403 instead of 401. Hopefully temporary code.
  return param.detail.status === 401 || param.detail.status === 403
}

function mergeAbortSignals(signals: AbortSignal[]) {
  const controller = new AbortController()

  function onAbort() {
    controller.abort()
    for (const signal of signals) {
      signal.removeEventListener("abort", onAbort)
    }
  }

  for (const signal of signals) {
    if (signal.aborted) {
      onAbort()
      break
    }
    signal.addEventListener("abort", onAbort)
  }

  return controller.signal
}

function isAbortError(error: unknown): error is DOMException {
  return error instanceof DOMException && error.name === "AbortError"
}

let getBearerToken: () => string = () => ""

// Externally register a way to provide a Bearer token, so this module remains decoupled from App concerns.
// Also this function could be called conditionally.
export function registerBearerTokenGetter(fn: () => string) {
  getBearerToken = fn
}

// Makes sure that in dev environments request credentials (cookies) aren't required (we use a header instead)
// Sending the credentials even though we use the Authorization header would lead to a CORS error.
// "include" ensures that credentials are always sent, whether the API is on the same exact origin (sub domains don't count) or not.
// This function is only needed when dealing with APIs hosted on a different origin than Page Builder, since the default of "same-origin" works well otherwise.
export function getRequestCredentials(
  authStrategy: "cookie" | "token"
): RequestInit["credentials"] {
  return authStrategy === "token" ? "omit" : "include"
}
